import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '@/types/user';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { profileApi, skillstreeApi } from '@/services/api';
import '../../pages/SkillstreeMicroskillPage.css';

interface LearningHistoryProps {
  user: User;
}

interface Microskill {
  micro_skill_id?: number;
  micro_skill_version_id?: number;
  title: string;
  learning_status?: string;
  learning_progress?: number;
  videos?: Array<{
    thumbnail?: {
      url?: string;
    };
    video?: {
      duration?: number;
    };
  }>;
}

interface EnrolledSkillstree {
  skillstree_id: number;
  skillstree_name: string;
  description?: string;
  image_url?: string;
  core_category_id: number;
  learning_progress?: number;
  versions?: Array<{
    micro_skills?: Microskill[];
  }>;
}

export const LearningHistory: React.FC<LearningHistoryProps> = ({ user }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('All');
  const [enrolledSkillstrees, setEnrolledSkillstrees] = useState<EnrolledSkillstree[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSkillstrees, setExpandedSkillstrees] = useState<Set<number>>(new Set());
  
  // Fetch enrolled skillstrees from API (same approach as SkillstreeMicroskillPage)
  useEffect(() => {
    const fetchEnrolledSkillstrees = async () => {
      try {
        setLoading(true);
        
        // Step 1: Fetch all skillstrees from /v1/skillstrees API to get list
        const skillstreesResponse = await skillstreeApi.getSkillstrees();
        const skillstreesData = (skillstreesResponse.data as any).data || [];
        
        // Step 2: Filter enrolled skillstrees that have at least one microskill with learning_status "in_progress" or "completed"
        const enrolledIds = skillstreesData
          .filter((st: any) => {
            // Must be enrolled
            if (st.is_enrolled !== true) return false;
            
            // Check if any microskill has learning_status of "in_progress" or "completed"
            if (st.versions && st.versions.length > 0) {
              const firstVersion = st.versions[0];
              if (firstVersion.micro_skills && Array.isArray(firstVersion.micro_skills)) {
                return firstVersion.micro_skills.some((ms: any) => 
                  ms.learning_status === 'in_progress' || ms.learning_status === 'completed'
                );
              }
            }
            
            return false;
          })
          .map((st: any) => st.skillstree_id);
        
        // Step 3: Fetch each skillstree detail individually to get videos (same as SkillstreeMicroskillPage)
        const skillstreesWithVideos = await Promise.all(
          enrolledIds.map(async (skillstreeId: number) => {
            try {
              const response = await skillstreeApi.getSkillstreeById(skillstreeId);
              const apiData = (response.data as any).data;
              
              if (!apiData) return null;
              
              // Get the first version's microskills (same as SkillstreeMicroskillPage)
              const firstVersion = apiData.versions && apiData.versions.length > 0 ? apiData.versions[0] : null;
              
              if (!firstVersion || !firstVersion.micro_skills) return null;
              
              // Map to our structure (same as SkillstreeMicroskillPage)
              return {
                skillstree_id: apiData.skillstree_id,
                skillstree_name: apiData.skillstree_name,
                description: apiData.description || '',
                image_url: apiData.image_url,
                core_category_id: apiData.core_category_id || skillstreesData.find((st: any) => st.skillstree_id === skillstreeId)?.core_category_id,
                learning_progress: firstVersion.learning_progress || 0,
                versions: [{
                  micro_skills: firstVersion.micro_skills.map((ms: any): Microskill => ({
                    micro_skill_id: ms.micro_skill_id,
                    micro_skill_version_id: ms.micro_skill_version_id,
                    title: ms.title || '',
                    learning_status: ms.learning_status,
                    learning_progress: ms.learning_progress,
                    videos: ms.videos || [], // Videos with thumbnails are included in detail endpoint
                  }))
                }]
              };
            } catch (error) {
              console.error(`Error fetching skillstree ${skillstreeId}:`, error);
              return null;
            }
          })
        );
        
        // Filter out any null results
        const validSkillstrees = skillstreesWithVideos.filter((st): st is EnrolledSkillstree => st !== null);
        
        setEnrolledSkillstrees(validSkillstrees);
      } catch (error) {
        console.error('Error fetching enrolled skillstrees:', error);
        setEnrolledSkillstrees([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEnrolledSkillstrees();
  }, []);

  // Helper function to get all videos from skillstree (same logic as SkillstreeMicroskillPage)
  const getAllVideos = (skillstree: EnrolledSkillstree): any[] => {
    const allVideos: any[] = [];
    
    if (skillstree.versions && skillstree.versions.length > 0) {
      const firstVersion = skillstree.versions[0];
      if (firstVersion.micro_skills && Array.isArray(firstVersion.micro_skills)) {
        firstVersion.micro_skills.forEach((microskill: any) => {
          if (microskill.videos && Array.isArray(microskill.videos)) {
            allVideos.push(...microskill.videos);
          }
        });
      }
    }
    
    return allVideos;
  };

  // Helper function to calculate sessions and hours
  const calculateStats = (skillstree: EnrolledSkillstree): { sessions: number; hours: number } => {
    let sessions = 0;
    let totalDurationSeconds = 0;
    
    if (skillstree.versions && skillstree.versions.length > 0) {
      const firstVersion = skillstree.versions[0];
      if (firstVersion.micro_skills && Array.isArray(firstVersion.micro_skills)) {
        firstVersion.micro_skills.forEach((microskill: any) => {
          if (microskill.videos && Array.isArray(microskill.videos)) {
            sessions += microskill.videos.length;
            microskill.videos.forEach((video: any) => {
              totalDurationSeconds += video.video?.duration || 0;
            });
          }
        });
      }
    }
    
    const hours = Math.round(totalDurationSeconds / 3600 * 10) / 10;
    return { sessions, hours };
  };

  // Toggle accordion expansion
  const toggleSkillstree = (skillstreeId: number) => {
    setExpandedSkillstrees(prev => {
      const newSet = new Set(prev);
      if (newSet.has(skillstreeId)) {
        newSet.delete(skillstreeId);
      } else {
        newSet.add(skillstreeId);
      }
      return newSet;
    });
  };

  // Get microskills for a skillstree
  const getMicroskills = (skillstree: EnrolledSkillstree): Microskill[] => {
    if (skillstree.versions && skillstree.versions.length > 0) {
      return skillstree.versions[0].micro_skills || [];
    }
    return [];
  };

  const renderEmptyState = () => (
    <div className="flex-1 flex flex-col items-center justify-center">
      <div className="mb-8">
        <img 
          src="/search-not-found.svg" 
          alt="No results found" 
          className="w-48 h-48 object-contain"
        />
      </div>
      <h3 className="text-white text-xl font-medium">No Enrolled Skillstrees</h3>
      <p className="text-gray-400 mt-2">You haven't enrolled in any skillstrees yet.</p>
    </div>
  );

  return (
    <div className="space-y-6 h-full learning-history-container">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-white text-2xl font-bold">Learning History</h1>
        
        {/* Filter Button */}
        {/* <Button
          variant="outline"
          className="bg-purple-600 border-purple-600 text-white hover:bg-purple-700 hover:border-purple-700 px-4 py-2 rounded-full flex items-center gap-2"
          onClick={() => {
            // Handle filter dropdown
          }}
        >
          <span className="text-sm font-medium">{filter}</span>
          <ChevronDown className="w-4 h-4" />
        </Button> */}
      </div>

      {/* Main Content Area */}
      <div className="flex h-full min-h-[600px]">
        {/* Content Area */}
        <div className="flex-1 flex">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-white">Loading...</div>
            </div>
          ) : enrolledSkillstrees.length === 0 ? (
            renderEmptyState()
          ) : (
            <div className="flex-1 p-6">
              {/* Enrolled Skillstrees List with Timeline */}
              <div className="relative">
                {/* Vertical Timeline Line */}
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-white/30"></div>
                
                <div className="space-y-4 pl-16">
                  {enrolledSkillstrees.map((skillstree, index) => {
                  // Get all videos from all microskills (same as SkillstreeMicroskillPage)
                  const allVideos = getAllVideos(skillstree);
                  const { sessions, hours } = calculateStats(skillstree);
                  const isExpanded = expandedSkillstrees.has(skillstree.skillstree_id);
                  const microskills = getMicroskills(skillstree);
                  
                  return (
                    <div key={skillstree.skillstree_id} className="space-y-0 relative">
                      {/* Timeline Node */}
                      <div className="absolute -left-10 top-6 w-4 h-4 rounded-full bg-purple-500 border-2 border-white z-10"></div>
                      
                      {/* Skillstree Card - Clickable to expand */}
                      <div 
                        className="microskill-detail-card cursor-pointer hover:bg-white/10 transition-colors"
                        onClick={() => toggleSkillstree(skillstree.skillstree_id)}
                      >
                        {/* Stacked Images on Left - Exact same logic as SkillstreeMicroskillPage */}
                        <div className="microskill-images-stack">
                          {allVideos && allVideos.length > 0
                            ? allVideos
                                .filter((video) => video.thumbnail?.url)
                                .slice(0, 4)
                                .map((video, imgIndex) => {
                                  const thumbnailPath = video.thumbnail?.url || '';
                                  return (
                                    <div key={imgIndex} className="microskill-thumbnail">
                                      <img 
                                        src={thumbnailPath}
                                        alt='thumbnail'
                                        className="microskill-thumbnail-image"
                                      />
                                    </div>
                                  );
                                })
                            : null}
                        </div>
                        
                        <div className="microskill-detail-content flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="microskill-detail-title text-left">{skillstree.skillstree_name}</h3>
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-white ml-2" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-white ml-2" />
                            )}
                          </div>
                          <div className="microskill-detail-info">
                            <div className="microskill-info-item">
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2 4H14V12H2V4Z" stroke="white" strokeWidth="1.5" fill="none"/>
                                <path d="M5 2V4M11 2V4" stroke="white" strokeWidth="1.5"/>
                                <path d="M2 7H14" stroke="white" strokeWidth="1.5"/>
                              </svg>
                              <span>{sessions} Sessions</span>
                            </div>
                            <div className="microskill-info-item">
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="8" cy="8" r="6" stroke="white" strokeWidth="1.5" fill="none"/>
                                <path d="M8 4V8L10 10" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                              <span>{hours} Hours</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Accordion Content - Microskills List */}
                      {isExpanded && microskills.length > 0 && (
                        <div className="bg-white/5 backdrop-blur-sm border-t border-white/10 rounded-b-lg overflow-hidden">
                          <div className="p-4 space-y-3">
                            {microskills.map((microskill, index) => {
                              const microskillVideos = microskill.videos || [];
                              const microskillSessions = microskillVideos.length;
                              const microskillHours = Math.round(
                                microskillVideos.reduce((sum: number, video: any) => sum + (video.video?.duration || 0), 0) / 3600 * 10
                              ) / 10;

                              return (
                                <div 
                                  key={microskill.micro_skill_id || microskill.micro_skill_version_id || index}
                                  className="bg-white/5 rounded-lg p-4 border border-white/10 hover:bg-white/10 transition-colors"
                                >
                                  <div className="flex items-start gap-4">
                                    {/* Video Thumbnails on Left - Only from this microskill's videos (show only first thumbnail) */}
                                    <div className="microskill-images-stack flex-shrink-0">
                                      {microskillVideos && microskillVideos.length > 0
                                        ? (() => {
                                            // Get the first video with a valid thumbnail from THIS microskill only
                                            const firstVideoWithThumbnail = microskillVideos.find(
                                              (video: any) => video.thumbnail?.url && video.thumbnail.url.trim() !== ''
                                            );
                                            
                                            if (firstVideoWithThumbnail) {
                                              const thumbnailPath = firstVideoWithThumbnail.thumbnail?.url || '';
                                              return (
                                                <div className="microskill-thumbnail">
                                                  <img 
                                                    src={thumbnailPath}
                                                    alt={`${microskill.title} thumbnail`}
                                                    className="microskill-thumbnail-image"
                                                    onError={(e) => {
                                                      // Hide broken images
                                                      (e.target as HTMLImageElement).style.display = 'none';
                                                    }}
                                                  />
                                                </div>
                                              );
                                            }
                                            return null;
                                          })()
                                        : null}
                                    </div>

                                    {/* Content on Right */}
                                    <div className="flex-1">
                                      <h4 className="text-white font-semibold text-base mb-2">{microskill.title}</h4>
                                      <div className="flex items-center gap-4 flex-wrap">
                                        <div className="microskill-info-item">
                                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2 4H14V12H2V4Z" stroke="white" strokeWidth="1.5" fill="none"/>
                                            <path d="M5 2V4M11 2V4" stroke="white" strokeWidth="1.5"/>
                                            <path d="M2 7H14" stroke="white" strokeWidth="1.5"/>
                                          </svg>
                                          <span>{microskillSessions} Sessions</span>
                                        </div>
                                        <div className="microskill-info-item">
                                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="8" cy="8" r="6" stroke="white" strokeWidth="1.5" fill="none"/>
                                            <path d="M8 4V8L10 10" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                                          </svg>
                                          <span>{microskillHours} Hours</span>
                                        </div>
                                        {microskill.learning_status && (
                                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                            microskill.learning_status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                            microskill.learning_status === 'in_progress' ? 'bg-orange-500/20 text-orange-400' :
                                            'bg-gray-500/20 text-gray-400'
                                          }`}>
                                            {microskill.learning_status === 'in_progress' ? 'In Progress' : 
                                             microskill.learning_status.charAt(0).toUpperCase() + microskill.learning_status.slice(1)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
